import React, { useEffect, useMemo, useState } from 'react';
import { kb } from '@/utils/api';
import { BookOpen, Plus, Trash2, X, Save, Search, FileCode, Tag, Filter, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const TEMPLATE_KINDS = ['sow', 'proposal', 'email'];

export default function KnowledgeBase() {
  const [tab, setTab] = useState('articles');
  const [articles, setArticles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiDesc, setAiDesc] = useState('');
  const [aiBusy, setAiBusy] = useState(false);

  const reload = () => {
    if (tab === 'articles') kb.articles(search).then(r => setArticles(r.data)).catch(() => {});
    else kb.templates(kindFilter).then(r => setTemplates(r.data)).catch(() => {});
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [tab, search, kindFilter]);

  const save = async () => {
    try {
      if (tab === 'articles') {
        if (editing.id) await kb.updateArticle(editing.id, editing);
        else await kb.createArticle(editing);
      } else {
        if (editing.id) await kb.updateTemplate(editing.id, editing);
        else await kb.createTemplate(editing);
      }
      toast.success('Saved');
      setEditing(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const del = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete?')) return;
    if (tab === 'articles') await kb.deleteArticle(id);
    else await kb.deleteTemplate(id);
    reload();
  };

  const newItem = () => setEditing(tab === 'articles'
    ? { title: '', category: 'General', body_md: '', tags: [] }
    : { kind: 'email', title: '', body_md: '', variables: [] });

  const runAi = async () => {
    if (!aiDesc.trim() || aiDesc.trim().length < 10) return toast.error('Describe what you want (10+ chars)');
    setAiBusy(true);
    try {
      const r = await kb.aiGenerate(aiDesc.trim(), tab === 'articles' ? 'article' : 'template', tab === 'templates' ? 'email' : undefined);
      setEditing(tab === 'articles'
        ? { title: r.data.title || 'AI draft', category: r.data.category || 'General', body_md: r.data.body_md || '', tags: r.data.tags || [] }
        : { kind: 'email', title: r.data.title || 'AI draft', body_md: r.data.body_md || '' });
      setAiOpen(false);
      setAiDesc('');
      toast.success('AI draft ready — review and save');
    } catch (e) { toast.error(e.response?.data?.detail || 'AI failed'); }
    finally { setAiBusy(false); }
  };

  const items = tab === 'articles' ? articles : templates;

  // Derived categories for sidebar filter
  const categories = useMemo(() => {
    if (tab !== 'articles') return [];
    const counts = {};
    for (const a of articles) {
      const c = (a.category || 'General').trim() || 'General';
      counts[c] = (counts[c] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [articles, tab]);

  const filteredItems = categoryFilter
    ? items.filter(a => (a.category || 'General') === categoryFilter)
    : items;

  return (
    <div className="p-6 animate-fade-in" data-testid="kb-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-black" style={{ color: 'var(--ink)' }}>Knowledge Base</h1>
          <p className="text-sm" style={{ color: 'var(--muted)' }}>Your private ops docs + reusable templates</p>
        </div>
        <div className="flex gap-2">
          <button className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-btn font-semibold" style={{ background: '#5B2D8E', color: 'white' }} onClick={() => setAiOpen(true)} data-testid="kb-ai-button">
            <Sparkles size={12} /> Describe with AI
          </button>
          <button className="btn-primary flex items-center gap-1.5 text-xs px-3 py-1.5" onClick={newItem} data-testid="kb-new-button">
            <Plus size={13} /> New {tab === 'articles' ? 'article' : 'template'}
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        {[{ k: 'articles', label: 'Articles', icon: BookOpen }, { k: 'templates', label: 'Templates', icon: FileCode }].map(({ k, label, icon: Icon }) => (
          <button key={k} onClick={() => { setTab(k); setCategoryFilter(''); }} className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1.5" style={{ background: tab === k ? '#006080' : 'var(--bg)', color: tab === k ? 'white' : 'var(--muted)', border: '1px solid var(--border)' }} data-testid={`kb-tab-${k}`}>
            <Icon size={12} /> {label}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-[180px_1fr] gap-5">
        {/* Sidebar: category filter (articles) or kind filter (templates) */}
        <div className="space-y-4">
          {tab === 'articles' ? (
            <>
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted)' }} />
                <input className="input-agency pl-8 text-sm" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} data-testid="kb-search" />
              </div>
              {categories.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5 flex items-center gap-1" style={{ color: 'var(--muted)' }}>
                    <Filter size={10} /> Categories
                  </p>
                  <div className="space-y-0.5" data-testid="kb-categories">
                    <button
                      onClick={() => setCategoryFilter('')}
                      className="w-full flex items-center justify-between text-xs font-semibold px-2.5 py-1.5 rounded-btn text-left"
                      style={{
                        background: !categoryFilter ? '#E6F1F4' : 'transparent',
                        color: !categoryFilter ? '#006080' : 'var(--muted)',
                      }}
                      data-testid="kb-cat-all"
                    >
                      <span>All</span>
                      <span className="font-mono" style={{ color: 'var(--muted)' }}>{articles.length}</span>
                    </button>
                    {categories.map(([c, count]) => (
                      <button
                        key={c}
                        onClick={() => setCategoryFilter(c)}
                        className="w-full flex items-center justify-between text-xs font-semibold px-2.5 py-1.5 rounded-btn text-left"
                        style={{
                          background: categoryFilter === c ? '#E6F1F4' : 'transparent',
                          color: categoryFilter === c ? '#006080' : 'var(--muted)',
                        }}
                        data-testid={`kb-cat-${c}`}
                      >
                        <span className="truncate">{c}</span>
                        <span className="font-mono flex-shrink-0 ml-2" style={{ color: 'var(--muted)' }}>{count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--muted)' }}>Kind</p>
              <div className="space-y-0.5">
                <button onClick={() => setKindFilter('')} className="w-full text-xs font-semibold px-2.5 py-1.5 rounded-btn text-left" style={{ background: !kindFilter ? '#E6F1F4' : 'transparent', color: !kindFilter ? '#006080' : 'var(--muted)' }}>All</button>
                {TEMPLATE_KINDS.map(k => (
                  <button key={k} onClick={() => setKindFilter(k)} className="w-full text-xs font-semibold px-2.5 py-1.5 rounded-btn text-left uppercase" style={{ background: kindFilter === k ? '#E6F1F4' : 'transparent', color: kindFilter === k ? '#006080' : 'var(--muted)' }}>
                    {k}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Items list */}
        <div>
          {filteredItems.length === 0 ? (
            <div className="card-base p-10 text-center">
              <BookOpen size={32} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>No {tab} {categoryFilter ? `in "${categoryFilter}"` : 'yet'}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Create your first {tab === 'articles' ? 'article' : 'template'} to get started</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3" data-testid="kb-list">
              {filteredItems.map(x => (
                <div key={x.id} className="card-interactive p-4 flex flex-col" onClick={() => setViewing(x)} data-testid={`kb-item-${x.id}`}>
                  <div className="flex items-start justify-between mb-1.5">
                    <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{x.title}</p>
                    <span className="badge-status flex-shrink-0 ml-2" style={{ background: '#E6F1F4', color: '#006080' }}>
                      {tab === 'articles' ? (x.category || 'General') : (x.kind || 'email').toUpperCase()}
                    </span>
                  </div>
                  <p className="text-xs line-clamp-3 flex-1" style={{ color: 'var(--muted)' }}>{(x.body_md || '').slice(0, 160)}…</p>
                  {x.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {x.tags.slice(0, 4).map(t => (
                        <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-sm flex items-center gap-0.5" style={{ background: 'var(--bg)', color: 'var(--muted)' }}>
                          <Tag size={7} /> {t}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-2 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>Updated {x.updated_at ? new Date(x.updated_at).toLocaleDateString() : '—'}</p>
                    <div className="flex gap-1">
                      <button onClick={(e) => { e.stopPropagation(); setEditing(x); }} className="text-xs" style={{ color: '#006080' }}>edit</button>
                      <button onClick={(e) => del(x.id, e)} className="text-xs" style={{ color: '#B34A3A' }} data-testid={`kb-del-${x.id}`}><Trash2 size={12} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* AI generator modal */}
      {aiOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} data-testid="kb-ai-modal">
          <div className="w-full max-w-lg rounded-card p-5" style={{ background: 'var(--surface)' }}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Sparkles size={16} style={{ color: '#5B2D8E' }} />
                <h2 className="font-black text-lg" style={{ color: 'var(--ink)' }}>Describe your {tab === 'articles' ? 'article' : 'template'}</h2>
              </div>
              <button onClick={() => setAiOpen(false)}><X size={18} style={{ color: 'var(--muted)' }} /></button>
            </div>
            <p className="text-xs mb-3" style={{ color: 'var(--muted)' }}>
              Claude Sonnet 4.5 drafts it for you. Review and save — nothing is published automatically.
            </p>
            <textarea
              className="input-agency text-sm resize-none"
              rows={7}
              placeholder={tab === 'articles'
                ? 'e.g. SOP for onboarding a new client. Cover: initial call agenda, must-have questions, how to create their project, setting up SMTP, and when to assign a project manager.'
                : 'e.g. Friendly chase email for clients who are 10+ days late on an invoice — firm but warm.'}
              value={aiDesc}
              onChange={(e) => setAiDesc(e.target.value)}
              data-testid="kb-ai-desc"
            />
            <div className="flex gap-2 mt-3">
              <button className="flex-1 py-2 text-sm rounded-btn border" onClick={() => setAiOpen(false)} disabled={aiBusy} style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>Cancel</button>
              <button className="flex-1 py-2 text-sm rounded-btn font-bold text-white flex items-center justify-center gap-1.5" style={{ background: '#5B2D8E' }} onClick={runAi} disabled={aiBusy || aiDesc.trim().length < 10} data-testid="kb-ai-run">
                {aiBusy ? 'Drafting…' : (<><Sparkles size={13} /> Generate</>)}
              </button>
            </div>
            <p className="text-[10px] mt-2" style={{ color: 'var(--muted)' }}>Needs a Claude API key in Settings → Integrations.</p>
          </div>
        </div>
      )}

      {/* Reader modal — 2-col: left sidebar summary, right body */}
      {viewing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={() => setViewing(null)} data-testid="kb-reader">
          <div className="w-full max-w-5xl rounded-card my-6 p-6" style={{ background: 'var(--surface)' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <span className="badge-status" style={{ background: '#E6F1F4', color: '#006080' }}>
                  {tab === 'articles' ? (viewing.category || 'General') : (viewing.kind || 'email').toUpperCase()}
                </span>
                <h2 className="font-black text-2xl" style={{ color: 'var(--ink)' }}>{viewing.title}</h2>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setEditing(viewing); setViewing(null); }} className="text-xs px-3 py-1.5 rounded-btn font-semibold" style={{ background: '#E6F1F4', color: '#006080' }}>Edit</button>
                <button onClick={() => setViewing(null)}><X size={18} style={{ color: 'var(--muted)' }} /></button>
              </div>
            </div>
            <div className="grid md:grid-cols-[200px_1fr] gap-6">
              <aside className="text-xs space-y-3" style={{ color: 'var(--muted)' }}>
                {viewing.tags?.length > 0 && (
                  <div>
                    <p className="font-bold uppercase tracking-wider mb-1">Tags</p>
                    <div className="flex flex-wrap gap-1">
                      {viewing.tags.map(t => (
                        <span key={t} className="px-1.5 py-0.5 rounded-sm" style={{ background: 'var(--bg)' }}>{t}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="font-bold uppercase tracking-wider mb-1">Updated</p>
                  <p>{viewing.updated_at ? new Date(viewing.updated_at).toLocaleString() : '—'}</p>
                </div>
                {viewing.created_at && (
                  <div>
                    <p className="font-bold uppercase tracking-wider mb-1">Created</p>
                    <p>{new Date(viewing.created_at).toLocaleString()}</p>
                  </div>
                )}
              </aside>
              <article className="prose prose-sm max-w-none whitespace-pre-wrap text-sm leading-relaxed" style={{ color: 'var(--ink)', fontFamily: 'Georgia, serif', lineHeight: 1.75 }}>
                {viewing.body_md || <span style={{ color: 'var(--muted)' }}>(empty)</span>}
              </article>
            </div>
          </div>
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-3xl rounded-card my-8 p-5" style={{ background: 'var(--surface)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-black text-lg" style={{ color: 'var(--ink)' }}>{editing.id ? 'Edit' : 'New'} {tab === 'articles' ? 'article' : 'template'}</h2>
              <button onClick={() => setEditing(null)} style={{ color: 'var(--muted)' }}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input className="input-agency text-sm" placeholder="Title" value={editing.title} onChange={e => setEditing({...editing, title: e.target.value})} data-testid="kb-title-input" />
              {tab === 'articles' ? (
                <>
                  <input className="input-agency text-sm" placeholder="Category (e.g. Playbooks, SOPs)" value={editing.category || ''} onChange={e => setEditing({...editing, category: e.target.value})} data-testid="kb-category-input" />
                  <input className="input-agency text-sm" placeholder="Tags (comma-separated)" value={(editing.tags || []).join(', ')} onChange={e => setEditing({...editing, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})} />
                </>
              ) : (
                <select className="input-agency text-sm" value={editing.kind} onChange={e => setEditing({...editing, kind: e.target.value})}>
                  {TEMPLATE_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              )}
              <textarea className="input-agency text-sm resize-none font-mono" style={{ fontSize: 12 }} rows={18} placeholder="Markdown content…" value={editing.body_md || ''} onChange={e => setEditing({...editing, body_md: e.target.value})} data-testid="kb-body-input" />
              <div className="flex gap-2 pt-2">
                <button className="flex-1 py-2 text-sm rounded-btn border" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={() => setEditing(null)}>Cancel</button>
                <button className="flex-1 btn-primary py-2 text-sm flex items-center justify-center gap-1.5" onClick={save} data-testid="kb-save"><Save size={14} /> Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
